import {
  action,
  computed,
  makeObservable,
  observable,
} from "mobx";

import {
  buildNutritionRecipeRecord,
  inflateNutritionRecipeRecord,
} from "./nutritionRecords";
import {
  createInitialFormState,
  getRecipeDerivedClassification,
  getRecipeDraftValidity,
  getRecipeTotals,
} from "../../nutrition/recipes/recipeLogic";
import {
  assertNutritionLimit,
  validateRecipeDraftLimits,
} from "../../nutrition/nutritionLimits";
import OptimisticNutritionEntity, {
  buildComparablePayload,
  NutritionEntitySaveMode,
  clonePlainValue,
  getOptimisticEntityAnnotations,
} from "./OptimisticNutritionEntity";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function fallbackTranslate(_key, fallbackValue) {
  return fallbackValue ?? "";
}

function buildRecipeDraft(recipeLike) {
  return createInitialFormState(recipeLike, fallbackTranslate);
}

export class NutritionRecipe extends OptimisticNutritionEntity {
  entityType = "recipe";
  id = "";
  order = 0;
  origin = null;
  createdAt = null;
  updatedAt = null;
  draft = buildRecipeDraft({});

  constructor(data = {}, options = {}) {
    super({
      store: options.store,
      saveMode: NutritionEntitySaveMode.DEBOUNCED,
      saveDelayMs: 1_200,
    });

    const draft = buildRecipeDraft(data);
    this.id = String(data?.id ?? "").trim().toLowerCase();
    this.order = Number(data?.order ?? 0) || 0;
    this.origin = data?.origin ?? null;
    this.createdAt = data?.createdAt ?? null;
    this.updatedAt = data?.updatedAt ?? null;
    this.draft = draft;
    this.initializeFromServerSnapshot(draft, { rev: data?._rev });

    makeObservable(this, {
      ...getOptimisticEntityAnnotations(),
      entityType: observable,
      id: observable,
      order: observable,
      origin: observable.ref,
      createdAt: observable.ref,
      updatedAt: observable.ref,
      draft: observable.ref,
      name: computed,
      description: computed,
      preparationTime: computed,
      imageUri: computed,
      typeKey: computed,
      meal_types: computed,
      ingredients: computed,
      items: computed,
      totals: computed,
      calories: computed,
      carbs: computed,
      protein: computed,
      fats: computed,
      fiber: computed,
      foods: computed,
      foodsPreview: computed,
      primaryMealType: computed,
      allergenCodes: computed,
      isVegetarian: computed,
      isVegan: computed,
      isClassificationComplete: computed,
      searchableText: computed,
      isValid: computed,
      replaceDraftSnapshot: action.bound,
      applyServerMetadata: action.bound,
      applyServerRecord: action.bound,
      tryApplyNoRefreshServerAck: action.bound,
      applyDraft: action.bound,
    });
  }

  static buildDraftFromRecord(record) {
    return buildRecipeDraft(inflateNutritionRecipeRecord(record));
  }

  static buildRecordFromDraft(draft, metadata = {}) {
    const record = buildNutritionRecipeRecord(
      {
        id: metadata.id,
        order: metadata.order,
        name: String(draft?.name ?? ""),
        description: String(draft?.description ?? ""),
        preparationTime: String(draft?.preparationTime ?? ""),
        imageUri: draft?.imageUri ?? null,
        typeKey: draft?.typeKey ?? "main-string",
        ingredients: Array.isArray(draft?.ingredients) ? draft.ingredients : [],
        origin: metadata.origin ?? null,
      },
      {
        createdAt: metadata.createdAt,
        updatedAt: metadata.updatedAt,
        rev: metadata.rev,
      },
    );

    assertNutritionLimit(validateRecipeDraftLimits(record));
    return record;
  }

  static fromRecord(record, options = {}) {
    return new NutritionRecipe(inflateNutritionRecipeRecord(record), options);
  }

  applyServerRecord(record) {
    this.applyServerMetadata(record);
    this.mergeServerSnapshot(this.buildDraftFromServerRecord(record), {
      rev: record?._rev,
    });
  }

  applyServerMetadata(record) {
    const runtimeRecipe = inflateNutritionRecipeRecord(record);
    this.order = Number(runtimeRecipe?.order ?? 0) || 0;
    this.origin = runtimeRecipe?.origin ?? null;
    this.createdAt = record?.createdAt ?? this.createdAt ?? null;
    this.updatedAt = record?.updatedAt ?? this.updatedAt ?? null;
    return runtimeRecipe;
  }

  buildDraftFromServerRecord(record) {
    return buildRecipeDraft(inflateNutritionRecipeRecord(record));
  }

  buildComparablePersistedPayloadFromDraft(draft = this.draft, options = {}) {
    return buildComparablePayload(
      NutritionRecipe.buildRecordFromDraft(draft, {
        id: options.id ?? this.id,
        order: options.order ?? this.order,
        origin: options.origin ?? this.origin,
        createdAt: options.createdAt ?? this.createdAt,
        updatedAt: options.updatedAt ?? this.updatedAt,
        rev: options.rev ?? this._rev,
      }),
    );
  }

  buildComparablePersistedPayloadFromServer(record) {
    return this.buildComparablePersistedPayloadFromDraft(
      NutritionRecipe.buildDraftFromRecord(record),
      {
        id: record?.id ?? this.id,
        order: record?.order ?? this.order,
        origin: record?.origin ?? null,
        createdAt: record?.createdAt,
        updatedAt: record?.updatedAt,
        rev: record?._rev,
      },
    );
  }

  tryApplyNoRefreshServerAck(record, options = {}) {
    const comparablePayload = this.buildComparablePersistedPayloadFromServer(record);
    if (comparablePayload == null) {
      return false;
    }

    this.applyServerMetadata(record);
    return this.tryProcessComparableServerAck({
      nextSnapshot: this.buildDraftFromServerRecord(record),
      nextRev: record?._rev,
      comparablePayload,
      finalize: options.finalizePendingAck !== false,
    });
  }

  get isPersistable() {
    return getRecipeDraftValidity(this.draft).canSave;
  }

  getDraftSnapshot() {
    return clonePlainValue(this.draft);
  }

  replaceDraftSnapshot(snapshot) {
    this.draft = clonePlainValue(snapshot ?? buildRecipeDraft({}));
  }

  get name() {
    return String(this.draft?.name ?? "");
  }

  get description() {
    return String(this.draft?.description ?? "");
  }

  get preparationTime() {
    return String(this.draft?.preparationTime ?? "");
  }

  get imageUri() {
    return this.draft?.imageUri ?? null;
  }

  get typeKey() {
    return this.draft?.typeKey ?? "main-string";
  }

  get meal_types() {
    return [
      this.typeKey === "breakfast-string"
        ? "breakfast"
        : this.typeKey === "snack-string"
          ? "snack"
          : "main",
    ];
  }

  get ingredients() {
    return Array.isArray(this.draft?.ingredients) ? this.draft.ingredients : [];
  }

  get items() {
    return this.toFirestore().items;
  }

  get totals() {
    return getRecipeTotals(this.ingredients);
  }

  get calories() {
    return this.totals.calories;
  }

  get carbs() {
    return this.totals.carbs;
  }

  get protein() {
    return this.totals.protein;
  }

  get fats() {
    return this.totals.fats;
  }

  get fiber() {
    return this.totals.fiber;
  }

  get foods() {
    return this.ingredients.map((ingredient) => ingredient?.name).filter(hasText);
  }

  get foodsPreview() {
    return this.foods.slice(0, 3);
  }

  get primaryMealType() {
    return this.meal_types[0] ?? "main";
  }

  get allergenCodes() {
    return getRecipeDerivedClassification(this.ingredients).allergens;
  }

  get isVegetarian() {
    return getRecipeDerivedClassification(this.ingredients).isVegetarian;
  }

  get isVegan() {
    return getRecipeDerivedClassification(this.ingredients).isVegan;
  }

  get isClassificationComplete() {
    const classification = getRecipeDerivedClassification(this.ingredients);
    return (
      classification.allergenDataComplete === true &&
      classification.dietaryDataComplete === true
    );
  }

  get searchableText() {
    return [this.name, this.description, ...this.foods]
      .filter(hasText)
      .join(" ")
      .toLowerCase();
  }

  get isValid() {
    return getRecipeDraftValidity(this.draft).canSave;
  }

  applyDraft(nextDraft, options = {}) {
    this.applyDraftSnapshot(nextDraft, options);
  }

  buildPersistableRecord(options = {}) {
    return buildNutritionRecipeRecord(
      {
        id: this.id,
        order: this.order,
        name: this.name,
        description: this.description,
        preparationTime: this.preparationTime,
        imageUri: this.imageUri,
        typeKey: this.typeKey,
        ingredients: this.ingredients,
        origin: this.origin,
      },
      {
        createdAt: options.createdAt ?? this.createdAt,
        updatedAt: options.updatedAt ?? this.updatedAt,
        rev: options.rev ?? this._rev,
      },
    );
  }

  toFirestore(options = {}) {
    return this.buildPersistableRecord(options);
  }

  toPlanSnapshot({ multiplier = 1, id = this.id } = {}) {
    const snapshot = {
      id,
      type: "recipe",
      name: this.name,
      description: this.description || "",
      multiplier,
      items: this.toFirestore().items,
      ...(this.origin ? { origin: this.origin } : {}),
    };

    if (hasText(this.imageUri)) {
      snapshot.image_uri = String(this.imageUri).trim();
    }

    return snapshot;
  }
}

export default NutritionRecipe;
